import type { Faction, Unit, ListCategory, SubOrder, KnightlyVirtue } from '../types/faction';
import type { ArmyList, ArmyEntry } from '../types/army';
import { findDuplicateMagicItems } from './magicItemUniqueness';
import { magicArmourAllowed } from './magicItemEligibility';
import { isOncePerArmy, maxPerModel, upgradeEligibility } from './upgradeEligibility';

/**
 * Whether a unit's `unlocked_by` condition currently holds. Units without the
 * flag are always available; the flag exists only on the duplicate rows that
 * represent a promoted slot.
 */
export function unitUnlockMet(unit: Unit, army: ArmyList): boolean {
  const u = unit.unlocked_by;
  if (!u) return true;
  if (u.required_honour) {
    return army.entries.some((e) => e.selectedVirtueId === u.required_honour);
  }
  if (u.general_unit_ids?.length) return generalMatches(army, u.general_unit_ids);
  if (u.character_unit_ids?.length) {
    return army.entries.some((e) => u.character_unit_ids!.includes(e.unitId));
  }
  return true;
}

/** Whether a composition restricts its roster to the units its list names */
export function isArmyOfInfamy(composition?: { source?: string; is_army_of_infamy?: boolean }): boolean {
  if (!composition) return false;
  return composition.is_army_of_infamy ?? composition.source === 'arcane_journal';
}

/** All single-select honour-family upgrades for a faction (Virtues, Honours, Origins, Disciplines) */
export function honourPool(faction: Faction): KnightlyVirtue[] {
  return [
    ...(faction.knightly_virtues ?? []),
    ...(faction.elven_honours ?? []),
    ...(faction.infamous_origins ?? []),
    ...(faction.disciplines_of_the_old_ones ?? []),
    ...(faction.big_names ?? []),
  ];
}

/** All budgeted upgrade-family upgrades for a faction (Gifts, Mutations, Spites, Powers, Traits) */
export function upgradeFamilyPool(faction: Faction): KnightlyVirtue[] {
  return [
    ...(faction.gifts_of_chaos ?? []),
    ...(faction.chaos_mutations ?? []),
    ...(faction.forest_sprites ?? []),
    ...(faction.vampiric_powers ?? []),
    ...(faction.chaotic_traits ?? []),
    ...(faction.daemonic_gifts ?? []),
    ...(faction.incantation_scrolls ?? []),
    // Disciplines are single-select honours in the base Lizardmen lists, but the
    // Renegades draft sells them as a budget ("Purchase 0-3 ... up to 100 points"),
    // so they belong to both pools; the two selection paths never overlap.
    ...(faction.disciplines_of_the_old_ones ?? []),
  ];
}

/**
 * What one purchase of a budgeted upgrade costs on this entry. Most upgrades
 * have a single price; dual-priced ones (Chaotic Traits, AJ Razing of
 * Westerland) charge characters `points` and units `unit_points` - per model
 * when `unit_points_per_model` (Unnatural Fortitude +2/model), else per unit
 * (Battle Hunger +20/unit).
 */
export function upgradeCost(up: KnightlyVirtue, unit: Unit, quantity: number): number {
  if (unit.category === 'character' || up.unit_points === undefined) return up.points;
  return up.unit_points_per_model ? up.unit_points * quantity : up.unit_points;
}

const UPGRADE_ALLOWANCE_RE =
  /gifts of chaos|chaos mutations|forest spites|vampiric powers|chaotic traits|daemonic gifts|incantation scrolls|disciplines? of the old ones/i;

/** Allowance-option phrase → the Faction key holding that family's upgrades */
export const UPGRADE_FAMILY_KEYS = [
  { re: /gifts of chaos/i, key: 'gifts_of_chaos' },
  { re: /chaos mutations/i, key: 'chaos_mutations' },
  { re: /forest spites/i, key: 'forest_sprites' },
  { re: /vampiric powers/i, key: 'vampiric_powers' },
  { re: /chaotic traits/i, key: 'chaotic_traits' },
  { re: /daemonic gifts/i, key: 'daemonic_gifts' },
  { re: /incantation scrolls/i, key: 'incantation_scrolls' },
  { re: /disciplines? of the old ones/i, key: 'disciplines_of_the_old_ones' },
] as const;

export type UpgradeFamilyKey = (typeof UPGRADE_FAMILY_KEYS)[number]['key'];

/** Which family an allowance option grants, from its description */
export function allowanceFamilyKey(opt: { description: string }): UpgradeFamilyKey | undefined {
  return UPGRADE_FAMILY_KEYS.find((f) => f.re.test(opt.description))?.key;
}

/**
 * ALL budgeted upgrade-family allowances on a unit. A unit may have several -
 * a Chaos Lord has both a Gifts of Chaos budget and a Chaotic Traits count -
 * so anything consuming this must partition selections by family.
 */
export function upgradeAllowanceOpts(unit: Unit) {
  return (unit.options ?? []).filter(
    (o) =>
      (o.max_points !== undefined || o.max_count !== undefined) &&
      UPGRADE_ALLOWANCE_RE.test(o.description),
  );
}

/** The unit option that grants a budgeted upgrade-family allowance, if any */
export function upgradeAllowanceOpt(unit: Unit) {
  return upgradeAllowanceOpts(unit)[0];
}


/**
 * Normalise a unit's equipment field into a flat string array.
 * Handles: string[] (most units), {rider/crew/mount: string[]} objects (cavalry/chariots), null/undefined.
 */
export function flattenEquipment(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as string[];
  if (typeof raw === 'object') {
    return Object.values(raw as Record<string, string[]>).flat();
  }
  return [];
}

/**
 * Calculate armour save from equipment strings and special rule IDs.
 * Returns e.g. "4+" or "-" if no armour present.
 * Barding (from mount equipment) should be included in the equipment array.
 * Accepts a flat string[] or the raw equipment field (object/null) from unit data.
 */
export function calcArmourSave(equipment: unknown, specialRuleIds: string[] = [], troopType?: string): string {
  const { base, mods } = armourComponents(equipment, specialRuleIds);
  return renderSave(base, mods, troopType);
}

/**
 * Maximum armour value by troop type (p.221, as errata'd by the Jan 2026
 * rulebook FAQ): 'chariot', 'monster' or 'war machine' can never be better
 * than 3+; 'infantry' or 'cavalry' (monstrous included) never better than 2+.
 */
function maxArmourValue(troopType?: string): number {
  return troopType && /chariot|behemoth|monstrous creature|war machine/i.test(troopType) ? 3 : 2;
}

/**
 * p.167/p.221: a model that wears no armour counts as having an armour value
 * of 7+ for the purposes of rules that improve armour value - so Armoured
 * Hide, a lone shield or barding still produce a save.
 */
function renderSave(base: number | null, mods: number, troopType?: string): string {
  const value = (base ?? 7) - mods;
  if (value >= 7) return '-';
  return `${Math.max(maxArmourValue(troopType), value)}+`;
}

/** The numeric pieces of an armour save: the suit's value, the total of
 *  shield/barding/hide modifiers, and whether a shield contributes (a model
 *  has one shield slot, so a magic shield must not stack with a mundane one). */
function armourComponents(equipment: unknown, specialRuleIds: string[] = []): { base: number | null; mods: number; hasShield: boolean } {
  const equip = flattenEquipment(equipment).map((e) => e.toLowerCase());

  let base: number | null = null;
  if (equip.some((e) => e.includes('full plate'))) base = 4;
  else if (equip.some((e) => e.includes('heavy armour') || e.includes('heavy armor'))) base = 5;
  else if (equip.some((e) => e.includes('light armour') || e.includes('light armor'))) base = 6;

  let mods = 0;
  const hasShield = equip.some((e) => e.includes('shield'));
  if (hasShield) mods += 1;
  if (equip.some((e) => e.includes('barding'))) mods += 1;

  // Armoured Hide (X) in special rule IDs, e.g. "armoured_hide_1" or "armoured_hide(1)"
  for (const rule of specialRuleIds) {
    const m = rule.match(/armoured[_\s]?hide[_\s(]?(\d+)/i);
    if (m) mods += parseInt(m[1], 10);
  }
  return { base, mods, hasShield };
}

/** Every free-equipment description any of the faction's honours could have
 *  written into selectedOptions (format: "Name (Honour Name)"). */
function honourFreeEquipDescs(faction: Faction): Set<string> {
  const out = new Set<string>();
  for (const h of honourPool(faction)) {
    for (const fe of h.free_equipment ?? []) out.add(`${fe.name} (${h.name})`);
  }
  return out;
}

/**
 * The armour save an entry's current selections produce - base equipment with
 * "replace X with Y" swaps applied, plus any selected option that grants
 * armour, a shield or barding (matched on the text: the Chosen's option is
 * "Shields", Beastmen buy "Light armour" from a choice group), the selected
 * honour's free equipment, and the mount's kit.
 */
export function entryArmourSave(unit: Unit, entry: ArmyEntry, faction: Faction): string {
  let equip = flattenEquipment(unit.equipment);
  for (const opt of entry.selectedOptions) {
    // Both phrasings occur: "Replace heavy armour with full plate armour"
    // and "May replace heavy armour with full plate armour"
    const m = opt.match(/^(?:may )?replace (.+?) with (.+)$/i);
    if (m) equip = equip.map((e) => (e.toLowerCase() === m[1].toLowerCase() ? m[2] : e));
  }

  const mount = entry.selectedMountId
    ? faction.units.find((u) => u.id === entry.selectedMountId)
    // Some characters buy the mount as a plain checkbox/choice option instead
    : faction.units.find((u) =>
        u.category === 'mount' &&
        entry.selectedOptions.some((o) => {
          const ol = o.toLowerCase();
          const nl = u.name.toLowerCase();
          return ol === nl || ol.endsWith(nl);
        }));
  const internalMountHasBarding = unit.profiles.some((pe) => pe.is_mount && /bard/i.test(pe.name));
  const honourEquip = honourFreeEquipDescs(faction);

  const extra = [
    // armourComponents only tests presence, so appending a duplicate is
    // harmless. \bshields?\b keeps "Shieldwall" (a formation rule) and
    // "Shieldbearers" (a mount) from reading as a shield.
    ...entry.selectedOptions.filter(
      (o) => /\bshields?\b|armour|armor|barding/i.test(o) && !/^(?:may )?replace /i.test(o),
    ),
    ...entry.selectedOptions.filter((o) => honourEquip.has(o)),
    ...flattenEquipment(mount?.equipment),
    ...(internalMountHasBarding ? ['Barding'] : []),
  ];
  // The ACTIVE rule set, not just the printed one: a Gnarled Hide mutation or
  // an honour can grant Armoured Hide (X)
  let { base, mods, hasShield } = armourComponents([...equip, ...extra], getEntryRuleIds(unit, entry, faction));

  // Magic armour: an item's armour_profile carries either the suit's value
  // ("5+"), a worn-with-other-armour modifier ("+1"), or a shield.
  let itemMods = 0;
  for (const itemId of entry.selectedMagicItemIds ?? []) {
    const item = faction.magic_items.find((i) => i.id === itemId);
    const ap = item?.armour_profile;
    if (!ap?.armour_value) continue;
    const av = String(ap.armour_value).trim();
    const isShieldItem = ap.type === 'shield' || item?.is_shield || /^shield$/i.test(av);
    if (isShieldItem) {
      // One shield slot: a magic shield replaces a mundane one
      if (!hasShield) { mods += 1; hasShield = true; }
      continue;
    }
    const plus = /^\+(\d)/.exec(av);
    if (plus) { itemMods += Number(plus[1]); continue; }
    const abs = /(\d)\s*\+/.exec(av);
    if (abs) {
      const v = Number(abs[1]);
      // "cannot be improved or reduced" (Meteoric Iron, Silvered Steel)
      if (ap.special_rules?.some((r) => /unmodifiable|unimprovable/.test(r))) return `${v}+`;
      // The magic suit replaces the mundane one - take the better value
      base = base === null ? v : Math.min(base, v);
    }
  }
  return renderSave(base, mods + itemMods, unit.troop_type);
}

/** Parse a unit_size string into { min, max } model counts */
export function parseUnitSize(unitSize: string): { min: number; max: number | null } {
  if (unitSize === '1') return { min: 1, max: 1 };
  const range = unitSize.match(/^(\d+)-(\d+)$/);
  if (range) return { min: parseInt(range[1]), max: parseInt(range[2]) };
  const min = unitSize.match(/^(\d+)\+$/);
  if (min) return { min: parseInt(min[1]), max: null };
  return { min: 1, max: null };
}

/** Whether a unit uses per-model points (vs a fixed total) */
export function isPerModelPoints(unit: Unit): boolean {
  return unit.unit_size !== '1';
}

/** Points cost for selected options (equipment, vows, virtues, mount, magic items) on a single entry */
export function calcOptionsCost(unit: Unit, entry: ArmyEntry, faction: Faction): number {
  let cost = 0;

  // Equipment / weapon / vow options
  if (unit.options) {
    for (const opt of unit.options) {
      if (opt.max_points !== undefined) continue; // allowance info only
      if (opt.choices) {
        // Choice group: find the selected choice (if any) and count its cost
        for (const choice of opt.choices) {
          if (entry.selectedOptions.includes(choice.description)) {
            const multiplier = choice.scope === 'per_model' && isPerModelPoints(unit) ? entry.quantity : 1;
            cost += choice.points * multiplier;
          }
        }
      } else if (opt.per_n_models !== undefined) {
        const qty = entry.optionQuantities?.[opt.description] ?? 0;
        cost += opt.points * qty;
      } else {
        if (!entry.selectedOptions.includes(opt.description)) continue;
        const multiplier = opt.scope === 'per_model' && isPerModelPoints(unit) ? entry.quantity : 1;
        cost += opt.points * multiplier;
      }
    }
  }

  // Honour-family upgrade (Knightly Virtue, Elven Honour, …)
  if (entry.selectedVirtueId) {
    const virtue = honourPool(faction)
      .find((v) => v.id === entry.selectedVirtueId);
    if (virtue) cost += virtue.points;
  }

  // Mount
  if (entry.selectedMountId) {
    const mount = faction.units.find((u) => u.id === entry.selectedMountId);
    if (mount) cost += mount.points;
  }

  // Budgeted upgrade-family upgrades (Gifts of Chaos, Mutations, Spites, Powers, Traits)
  if (entry.selectedUpgradeIds?.length) {
    const pool = upgradeFamilyPool(faction);
    for (const upId of entry.selectedUpgradeIds) {
      const up = pool.find((u) => u.id === upId);
      if (up) cost += upgradeCost(up, unit, entry.quantity);
    }
  }

  // Magic items (personal items + magic standards)
  for (const itemId of entry.selectedMagicItemIds ?? []) {
    const item = faction.magic_items.find((i) => i.id === itemId);
    if (item) cost += item.points;
  }

  // BSB unlimited magic standard (tracked separately, no points cap)
  if (entry.selectedBsbStandardId) {
    const item = faction.magic_items.find((i) => i.id === entry.selectedBsbStandardId);
    if (item) cost += item.points;
  }

  // Runic items (Dwarfs only)
  const ri = entry.runicItems;
  if (ri && faction.runic_items) {
    const allRuneIds = [
      ...ri.weaponRunes,
      ...ri.armourRunes,
      ...ri.talismanRunes,
      ...ri.engineeringRunes,
      ...ri.tattooRunes,
    ];
    for (const runeId of allRuneIds) {
      const rune = faction.runic_items.find((r) => r.id === runeId);
      if (rune) cost += rune.points;
    }
  }

  return cost;
}

/** Points cost of a single entry, based on quantity, command upgrades, and selected options */
export function calcEntryPoints(unit: Unit, entry: ArmyEntry, faction?: Faction): number {
  const base = isPerModelPoints(unit)
    ? unit.points * entry.quantity
    : unit.points;

  let cmd = 0;
  if (unit.command) {
    const champions = unit.command.filter(c => c.role === 'champion');
    for (let i = 0; i < champions.length; i++) {
      const c = champions[i];
      const selected = i === 0 ? entry.includeChampion : (c.name != null && entry.selectedOptions.includes(c.name));
      if (selected) cmd += c.cost_per_unit;
    }
    for (const c of unit.command) {
      if (c.role === 'standard_bearer' && entry.includeStandard) { cmd += c.cost_per_unit; break; }
    }
    for (const c of unit.command) {
      if (c.role === 'musician' && entry.includeMusician) { cmd += c.cost_per_unit; break; }
    }
  }
  const opts = faction ? calcOptionsCost(unit, entry, faction) : 0;
  return base + cmd + opts;
}

/**
 * Returns the list category a unit counts toward for a specific army composition,
 * respecting any composition-specific overrides.
 * Returns null for mounts (not standalone units) and units with no category.
 */
export function getEffectiveListCategory(
  unit: Unit,
  compositionId: string,
  isAoI = false,
  subOrder?: SubOrder
): ListCategory | null {
  if (unit.category === 'mount') return null;
  // Sub-order unlocks take highest priority (e.g. Teutogen Guard unlocked by White Wolf)
  if (subOrder) {
    const unlock = subOrder.unlocks.find((u) => u.unit_id === unit.id);
    if (unlock) return unlock.list_category;
  }
  // Apply composition-specific override if present (null = explicitly excluded)
  if (unit.list_category_overrides && compositionId in unit.list_category_overrides) {
    return unit.list_category_overrides[compositionId] ?? null;
  }
  // AoI compositions require an explicit override - no implicit fallback for non-character units
  if (isAoI && unit.category !== 'character') return null;
  // Characters default to 'characters' slot but respect an explicit list_category
  // (e.g. Doomseekers are character-typed but live in mercenaries)
  if (unit.category === 'character') return (unit.list_category as ListCategory | null) ?? 'characters';
  return unit.list_category ?? null;
}

export interface CategoryPoints {
  characters: number;
  core: number;
  special: number;
  rare: number;
  mercenaries: number;
  total: number;
}

/** Compute points per category, using composition-aware slot assignments */
/**
 * Whether the army's General is one of general_unit_ids. When the player has
 * designated a General, that entry must match; otherwise fall back to mere
 * presence in the army, so an undesignated list is not flagged prematurely.
 */
export function generalMatches(army: ArmyList, generalUnitIds: string[]): boolean {
  const genEntry = army.generalEntryId
    ? army.entries.find((e) => e.id === army.generalEntryId)
    : undefined;
  if (genEntry) return generalUnitIds.includes(genEntry.unitId);
  return army.entries.some((e) => generalUnitIds.includes(e.unitId));
}

/**
 * Whether a conditional_honour_* rule's condition holds: a character entry has the
 * required honour selected, or (for general_unit_ids rules) the army's General is
 * one of those units.
 */
export function conditionalHonourApplies(
  rule: { required_honour?: string; general_unit_ids?: string[]; character_unit_ids?: string[] },
  army: ArmyList,
  faction: Faction,
): boolean {
  if (rule.required_honour) {
    return army.entries.some((e) => {
      const u = faction.units.find((uu) => uu.id === e.unitId);
      return u?.category === 'character' && e.selectedVirtueId === rule.required_honour;
    });
  }
  if (rule.general_unit_ids?.length) {
    return generalMatches(army, rule.general_unit_ids);
  }
  // "if your army includes one or more Death Hags" - presence, not the General
  if (rule.character_unit_ids?.length) {
    return army.entries.some((e) => rule.character_unit_ids!.includes(e.unitId));
  }
  // No condition attached: the promotion is unconditional ("0-1 unit of Ushabti
  // may be taken as a Core choice"), so it always applies.
  return true;
}

export function calcCategoryPoints(army: ArmyList, faction: Faction): CategoryPoints {
  const result: CategoryPoints = { characters: 0, core: 0, special: 0, rare: 0, mercenaries: 0, total: 0 };
  const composition = faction.army_compositions.find((c) => c.id === army.compositionId);
  const isAoI = isArmyOfInfamy(composition);
  const subOrder = army.subOrderId
    ? composition?.sub_orders?.find((s) => s.id === army.subOrderId)
    : undefined;
  const rows: { unitId: string; pts: number; cat: ListCategory | null }[] = [];
  for (const entry of army.entries) {
    const unit = faction.units.find((u) => u.id === entry.unitId);
    if (!unit) continue;
    let pts = calcEntryPoints(unit, entry, faction);
    // Add sub-order upgrade costs
    if (subOrder) {
      if (subOrder.unit_ids.includes(unit.id)) {
        pts += subOrder.unit_upgrade_pts_per_model * entry.quantity;
      } else if (
        unit.category === 'character' &&
        (unit.id === 'grand_master' || unit.id === 'chapter_master')
      ) {
        pts += subOrder.character_upgrade_pts;
      }
    }
    const cat = getEffectiveListCategory(unit, army.compositionId, isAoI, subOrder);
    rows.push({ unitId: unit.id, pts, cat: cat ?? null });
  }
  // Honour-conditional slots: while the condition holds, up to N eligible units
  // count as the rule's category instead of their default (e.g. Lion Chariot as
  // Special when a character has the Chracian Hunter Elven Honour)
  for (const rule of composition?.rules ?? []) {
    if (rule.limit_type !== 'conditional_honour_count' && rule.limit_type !== 'conditional_honour_per_1000_pts') continue;
    if (!rule.unit_ids?.length || !conditionalHonourApplies(rule, army, faction)) continue;
    let cap = rule.limit_type === 'conditional_honour_per_1000_pts'
      ? Math.max(1, Math.floor(army.pointsLimit / 1000)) * rule.limit_value
      : rule.limit_value;
    for (const row of rows) {
      if (cap <= 0) break;
      if (rule.unit_ids.includes(row.unitId) && row.cat !== rule.category) {
        row.cat = rule.category as ListCategory;
        cap -= 1;
      }
    }
  }
  for (const row of rows) {
    if (row.cat) result[row.cat] += row.pts;
    result.total += row.pts;
  }
  return result;
}

export interface ValidationIssue {
  category: string;
  message: string;
  severity: 'error' | 'warning';
}

/**
 * Returns true if a unit is a Wizard (can take arcane items).
 * Used to enforce: non-magic characters may not take arcane items.
 */
export function isWizard(unit: Unit): boolean {
  return (unit.magic?.wizard_level ?? 0) > 0;
}

/**
 * Whether this entry counts as a Wizard once its selections are taken into
 * account. A model can become one four ways: on its profile, by taking a wizard
 * upgrade option, through an honour-family upgrade that grants levels, or by
 * wearing an item that does (the Wizarding Hat). Sorcerous Ruses and the arcane
 * item slot both key off this.
 */
export function entryIsWizard(unit: Unit, entry: ArmyEntry, faction: Faction): boolean {
  if (isWizard(unit)) return true;

  const becomesWizard = (text: string) => /level \d+ wizard|be a wizard/i.test(text);
  for (const opt of unit.options ?? []) {
    if (entry.selectedOptions.includes(opt.description) && becomesWizard(opt.description)) return true;
    for (const c of opt.choices ?? []) {
      if (entry.selectedOptions.includes(c.description) && becomesWizard(c.description)) return true;
    }
  }

  if (entry.selectedVirtueId) {
    const honour = honourPool(faction).find((h) => h.id === entry.selectedVirtueId);
    if ((honour?.wizard_level ?? 0) > 0) return true;
  }

  for (const itemId of entry.selectedMagicItemIds ?? []) {
    const item = faction.magic_items.find((i) => i.id === itemId);
    if (((item as { grants_wizard_level?: number } | undefined)?.grants_wizard_level ?? 0) > 0) return true;
  }

  return false;
}

/** Read a unit's Leadership from its first non-mount profile. Returns 0 if unknown. */
export function getUnitLeadership(unit: Unit): number {
  const prof = (unit.profiles ?? []).find((p) => !p.is_mount) ?? unit.profiles?.[0];
  const ld = prof?.profile?.Ld;
  const n = typeof ld === 'number' ? ld : parseInt(String(ld ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Unit Strength per model by troop type, from the Troop Type tables in the
 * Warhammer: the Old World rulebook (pp. 191-198). Monstrous creatures,
 * behemoths and war machines instead use their starting Wounds.
 */
const US_PER_MODEL: Record<string, number> = {
  'regular infantry': 1,
  'heavy infantry': 1,
  'monstrous infantry': 3,
  swarms: 3,
  'light cavalry': 2,
  'heavy cavalry': 2,
  'monstrous cavalry': 3,
  'war beasts': 1,
  'light chariot': 3,
  'heavy chariot': 5,
};

const US_FROM_WOUNDS = ['monstrous creature', 'behemoth', 'war machine'];

/** Read a unit's starting Wounds from its first non-mount profile. */
function getUnitWounds(unit: Unit): number {
  const prof = (unit.profiles ?? []).find((p) => !p.is_mount) ?? unit.profiles?.[0];
  const w = prof?.profile?.W;
  const n = typeof w === 'number' ? w : parseInt(String(w ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Unit Strength for an entry at its current model count. Returns 0 when the
 * troop type is not recognised, so callers treat it as "unknown" rather than
 * flagging a false breach.
 */
export function calcUnitStrength(unit: Unit, quantity: number): number {
  const tt = (unit.troop_type ?? '').toLowerCase();
  if (US_FROM_WOUNDS.some((k) => tt.includes(k))) return getUnitWounds(unit);
  const key = Object.keys(US_PER_MODEL).find((k) => tt.includes(k));
  if (!key) return 0;
  return US_PER_MODEL[key] * Math.max(1, quantity);
}

/** True if this entry is upgraded to a Battle Standard Bearer. */
export function isBattleStandardBearer(entry: ArmyEntry): boolean {
  // The upgrade's exact wording varies by faction: "Upgrade to Battle Standard
  // Bearer", "Battle Standard Bearer", "Battle Standard Bearer (+25 pts, one
  // per army)" (Grand Cathay), "Upgrade to Battle Standard (Daemonic Locus)"
  // (Daemons of Chaos). Match the phrase rather than one literal string.
  return entry.selectedOptions.some((o) => /battle standard/i.test(o));
}

/** Character entries in an army, in list order. */
export function characterEntries(army: ArmyList, faction: Faction): ArmyEntry[] {
  return army.entries.filter((e) => {
    const u = faction.units.find((x) => x.id === e.unitId);
    return u?.category === 'character';
  });
}

/**
 * The entry that should be General by default: the highest-Leadership character
 * that is not the Battle Standard Bearer (ties broken by list order). Null if none.
 */
export function suggestedGeneralEntryId(army: ArmyList, faction: Faction): string | null {
  const candidates = characterEntries(army, faction).filter((e) => !isBattleStandardBearer(e));
  let best: { id: string; ld: number } | null = null;
  for (const e of candidates) {
    const u = faction.units.find((x) => x.id === e.unitId);
    if (!u) continue;
    const ld = getUnitLeadership(u);
    if (!best || ld > best.ld) best = { id: e.id, ld };
  }
  return best?.id ?? null;
}

/**
 * All special rule ids active on an entry: the unit's own rules (minus any the
 * selected honour removes) plus rules granted by selected options, magic items,
 * and the honour-family upgrade (Knightly Virtue / Elven Honour).
 */
export function getEntryRuleIds(unit: Unit, entry: ArmyEntry, faction: Faction): string[] {
  const honour = honourPool(faction)
    .find((v) => v.id === entry.selectedVirtueId);
  const removed = new Set(honour?.removes_rules ?? []);
  const ids = [...(unit.special_rules ?? [])];
  for (const o of unit.options ?? []) {
    // A mount-pointer option is never itself selected, so key it off the mount
    const active = isMountPointerOption(o.description)
      ? entryHasMount(entry)
      : entry.selectedOptions.includes(o.description);
    if (active) {
      ids.push(...(o.grants_rules ?? []));
      for (const r of o.removes_rules ?? []) removed.add(r);
    }
    for (const c of o.choices ?? []) {
      if (entry.selectedOptions.includes(c.description)) {
        ids.push(...(c.grants_rules ?? []));
        for (const r of c.removes_rules ?? []) removed.add(r);
      }
    }
  }
  for (const itemId of entry.selectedMagicItemIds ?? []) {
    const item = faction.magic_items.find((i) => i.id === itemId) as
      | { grants_rules?: string[] }
      | undefined;
    ids.push(...(item?.grants_rules ?? []));
  }
  // Budgeted upgrades (Chaotic Traits: Longstriders grants Vanguard, Brazen
  // Will grants Magic Resistance; likewise Gifts, Mutations, Powers…)
  if (entry.selectedUpgradeIds?.length) {
    const pool = upgradeFamilyPool(faction);
    for (const upId of new Set(entry.selectedUpgradeIds)) {
      const up = pool.find((u) => u.id === upId);
      ids.push(...(up?.grants_rules ?? []));
      for (const r of up?.removes_rules ?? []) removed.add(r);
    }
  }
  ids.push(...(honour?.grants_rules ?? []));
  // Removal wins over a grant, whatever order they were collected in; a rule
  // granted twice (e.g. base Terror plus a mark that grants Terror) shows once
  return [...new Set(ids)].filter((r) => !removed.has(r));
}

/**
 * Maximum points a unit may spend on a magic standard, or 0 if it may not take
 * one. Most units carry this as `magic_standard`, but many only express it as
 * the max_points of their "may purchase a magic standard" option - without this
 * fallback those units offered no magic standard picker at all.
 */
export function magicStandardCap(unit: Unit): number {
  if (unit.magic_standard) return unit.magic_standard;
  const opt = unit.options?.find(
    (o) => o.max_points !== undefined && /magic standard/i.test(o.description),
  );
  return opt?.max_points ?? 0;
}

/**
 * Returns true if a unit entry has an active standard bearer,
 * which is required to carry a magic standard.
 */
export function hasMagicStandardBearer(unit: Unit, entry: ArmyEntry): boolean {
  return magicStandardCap(unit) > 0 && entry.includeStandard;
}

/**
 * Whether an option is a pointer to the Character Mounts picker rather than a
 * priced mount in its own right ("Mount on a Great Taurus" costs points and is
 * a normal option). Matched case-insensitively - the faction data spells the
 * pointer both "see Character Mounts" and "see character mounts".
 */
export function isMountPointerOption(description: string): boolean {
  return (
    description === 'Mount' ||
    /^mount \(see character mounts\)$/i.test(description) ||
    /^may be mounted/i.test(description)
  );
}

/** True if this option/choice text is only legal on a mounted model */
export function optionRequiresMount(text: string): boolean {
  return /\(if appropriately mounted\)/i.test(text);
}

/** True if this option purchases a unit standard (magic standard / standard runes),
 *  which requires the unit to include a standard bearer */
export function optionRequiresStandardBearer(text: string): boolean {
  return /\b(magic standard|standard runes)\b/i.test(text);
}

/** True if the entry has any mount selected (mount row or a mount-granting option) */
export function entryHasMount(entry: ArmyEntry): boolean {
  return Boolean(entry.selectedMountId)
    || entry.selectedOptions.some((d) => /\bmount(ed)? on\b/i.test(d));
}

/** Validate the army list against composition rules and matched play formats */
export function validateArmy(army: ArmyList, faction: Faction): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const pts = calcCategoryPoints(army, faction);
  const limit = army.pointsLimit;
  const isBattleMarchList = army.matchedPlayFormats.includes('battle_march');
  /**
   * Battle March armies sit below 1,000 points, where a "0-X per 1,000 points"
   * cap would normally allow nothing at all. The book instead lets such a list
   * include a single such option, so each cap is relaxed to one here and the
   * army-wide total is checked in the Battle March block below.
   */
  const per1000 = (perThousand: number) =>
    isBattleMarchList
      ? Math.max(1, Math.floor(limit / 1000) * perThousand)
      : Math.floor(limit / 1000) * perThousand;

  // --- Over points limit ---
  if (pts.total > limit) {
    issues.push({
      category: 'Total',
      message: `Army is over points limit by ${pts.total - limit} pts`,
      severity: 'error',
    });
  }

  // --- General designation (must be a non-BSB character) ---
  const chars = characterEntries(army, faction);
  // Some armies are led by their magic: "the General of a Vampire Counts army
  // must also be a Wizard... If your army includes only a single Wizard, that
  // Wizard must be your General, even if they are not the character with the
  // highest Leadership" (Legends: Vampire Counts p.5). Recorded as a
  // general_must_be_wizard composition rule; when present, the highest-Ld
  // comparison only considers Wizards, since nothing else may lead.
  const generalMustBeWizard = (faction.army_compositions
    .find((c) => c.id === army.compositionId)?.rules ?? [])
    .some((r) => (r as { limit_type?: string }).limit_type === 'general_must_be_wizard');
  const entryIsWizardHere = (e: ArmyEntry) => {
    const u = faction.units.find((x) => x.id === e.unitId);
    return u ? entryIsWizard(u, e, faction) : false;
  };
  if (chars.length > 0) {
    const genEntry = army.generalEntryId
      ? army.entries.find((e) => e.id === army.generalEntryId)
      : undefined;
    if (!genEntry) {
      issues.push({
        category: 'General',
        message: 'No General designated. Mark a character (usually your highest-Leadership one) as the General.',
        severity: 'error',
      });
    } else {
      const genUnit = faction.units.find((u) => u.id === genEntry.unitId);
      if (genUnit?.category !== 'character') {
        issues.push({
          category: 'General',
          message: 'The designated General is not a character. Choose a character as your General.',
          severity: 'error',
        });
      }
      if (isBattleStandardBearer(genEntry)) {
        issues.push({
          category: 'General',
          message: 'The General cannot be the Battle Standard Bearer. Designate a different character as General.',
          severity: 'error',
        });
      }
      if (generalMustBeWizard && genUnit?.category === 'character' && !entryIsWizardHere(genEntry)) {
        issues.push({
          category: 'General',
          message: 'The General of this army must be a Wizard.',
          severity: 'error',
        });
      }
      // The General must have the highest Leadership in the army (ties are allowed).
      // Where the General must be a Wizard, only Wizards compete for the title.
      if (genUnit?.category === 'character') {
        const eligibleChars = generalMustBeWizard ? chars.filter(entryIsWizardHere) : chars;
        const charLds = (eligibleChars.length > 0 ? eligibleChars : chars).map((e) => {
          const u = faction.units.find((x) => x.id === e.unitId);
          return { entry: e, unit: u, ld: u ? getUnitLeadership(u) : 0 };
        });
        const maxLd = Math.max(...charLds.map((c) => c.ld));
        const genLd = getUnitLeadership(genUnit);
        if (genLd < maxLd) {
          const higher = charLds
            .filter((c) => c.ld === maxLd && c.entry.id !== genEntry.id)
            .map((c) => c.entry.customName || c.unit?.name)
            .filter((n): n is string => Boolean(n));
          const higherNames = Array.from(new Set(higher)).join(', ');
          issues.push({
            category: 'General',
            message: `The General must have the highest Leadership in the army (Ld ${maxLd})${higherNames ? `: ${higherNames} ${higher.length > 1 ? 'have' : 'has'} higher Leadership` : ''}. Designate a highest-Leadership character (or one of those tied) as General.`,
            severity: 'error',
          });
        }
      }
    }
  }

  // --- Battle Standard Bearer: a single character may carry the battle standard ---
  const bsbCount = army.entries.filter(isBattleStandardBearer).length;
  if (bsbCount > 1) {
    issues.push({
      category: 'General',
      message: `${bsbCount} Battle Standard Bearers - a single character in your army may be upgraded to carry the battle standard`,
      severity: 'error',
    });
  }

  // --- Unknown references (imported or hand-edited lists) ---
  // An entry naming a unit or item this faction does not have would otherwise
  // be silently skipped by every check below - it must be an error, not a
  // no-op. This is also what stops a Daemons list smuggling in Common magic
  // items: Daemons never receive the commons, so the ids are simply unknown.
  for (const entry of army.entries) {
    const unit = faction.units.find((u) => u.id === entry.unitId);
    if (!unit) {
      issues.push({
        category: 'Availability',
        message: `Unknown unit "${entry.unitId}" - not part of this army list`,
        severity: 'error',
      });
      continue;
    }
    for (const itemId of entry.selectedMagicItemIds ?? []) {
      if (!faction.magic_items.some((i) => i.id === itemId)) {
        issues.push({
          category: 'Magic items',
          message: `${unit.name}: unknown magic item "${itemId}" - not available to this army`,
          severity: 'error',
        });
      }
    }
  }

  // --- Unit availability (runs regardless of points) ---
  const availComposition = faction.army_compositions.find((c) => c.id === army.compositionId);
  const availIsAoI = isArmyOfInfamy(availComposition);
  const availSubOrder = army.subOrderId
    ? availComposition?.sub_orders?.find((s) => s.id === army.subOrderId)
    : undefined;
  const compName = availComposition?.name ?? army.compositionId;
  for (const entry of army.entries) {
    const unit = faction.units.find((u) => u.id === entry.unitId);
    if (!unit) continue;
    // A sub-order unlock trumps the availability tag (Teutogen Guard is
    // grand_army-only, except that the White Wolf brings it into a Knightly Order).
    const unlockedBySubOrder = availSubOrder?.unlocks.some((u) => u.unit_id === unit.id) ?? false;
    if (
      unit.availability?.length &&
      !unit.availability.includes(army.compositionId) &&
      !unlockedBySubOrder
    ) {
      issues.push({
        category: 'Availability',
        message: `${unit.name} is not available in ${compName}`,
        severity: 'error',
      });
      continue;
    }
    // Sub-order exclusions (e.g. a White Wolf army cannot include Priests of Sigmar)
    if (availSubOrder?.restrictions.includes(unit.id)) {
      issues.push({
        category: 'Availability',
        message: `${unit.name} cannot be included in a ${availSubOrder.name} army`,
        severity: 'error',
      });
      continue;
    }
    // Off-roster units: in an Army of Infamy every non-character unit needs a
    // slot given by the list (an override or a sub-order unlock); an explicit
    // null override excludes a unit from a specific composition anywhere
    // (e.g. no Tomb King in a Mortuary Cult). Implicit nulls outside AoIs are
    // attachment units (Fanatics, Weapon Teams) and stay untouched.
    if (unit.category !== 'mount') {
      const cat = getEffectiveListCategory(unit, army.compositionId, availIsAoI, availSubOrder);
      const explicitlyExcluded =
        unit.list_category_overrides &&
        army.compositionId in unit.list_category_overrides &&
        unit.list_category_overrides[army.compositionId] == null;
      if (cat === null && (availIsAoI || explicitlyExcluded)) {
        issues.push({
          category: 'Availability',
          message: `${unit.name} is not part of the ${compName} composition list`,
          severity: 'error',
        });
      }
    }
  }

  // --- Option constraints: required choice groups and mutual exclusions ---
  for (const entry of army.entries) {
    const unit = faction.units.find((u) => u.id === entry.unitId);
    if (!unit?.options) continue;
    const selected = entry.selectedOptions;

    for (const opt of unit.options) {
      // Required choice group: one of its choices must be selected (e.g. Forsaken god choice)
      if (opt.required && opt.choices?.length && !opt.choices.some((c) => selected.includes(c.description))) {
        issues.push({
          category: 'Options',
          message: `${unit.name}: must choose one of "${opt.description}"`,
          severity: 'error',
        });
      }
      // condition_not: this option/group cannot be combined with the named selection
      if (opt.condition_not && selected.includes(opt.condition_not)) {
        const taken = opt.choices?.length
          ? opt.choices.some((c) => selected.includes(c.description))
          : selected.includes(opt.description);
        if (taken) {
          issues.push({
            category: 'Options',
            message: `${unit.name}: "${opt.description}" cannot be combined with "${opt.condition_not}"`,
            severity: 'error',
          });
        }
      }
    }
  }

  // --- Budgeted upgrade families: allowance caps and once-per-army limits ---
  {
    const pool = upgradeFamilyPool(faction);
    const armyWideCounts = new Map<string, number>();
    for (const entry of army.entries) {
      // Once-per-army counts models carrying the upgrade, not copies of it, so a
      // model that (legally) takes two of something still only accounts for one.
      for (const id of new Set(entry.selectedUpgradeIds ?? [])) {
        armyWideCounts.set(id, (armyWideCounts.get(id) ?? 0) + 1);
      }
      const unit = faction.units.find((u) => u.id === entry.unitId);
      if (!unit || !entry.selectedUpgradeIds?.length) continue;
      const selected = entry.selectedUpgradeIds
        .map((id) => pool.find((u) => u.id === id))
        .filter((u): u is KnightlyVirtue => Boolean(u));
      // A unit may hold several allowances (a Chaos Lord has both a Gifts of
      // Chaos points budget and a Chaotic Traits count), so each allowance is
      // checked against only the selections belonging to ITS family.
      const allowances = upgradeAllowanceOpts(unit);
      // Upgrades only exist through an allowance option; a model without one
      // (e.g. a Tomb King holding Incantation Scrolls) may not buy any. Reachable
      // through imported or hand-edited lists - the picker never offers them.
      if (allowances.length === 0 && selected.length > 0) {
        issues.push({
          category: 'Upgrades',
          message: `${unit.name}: may not purchase ${[...new Set(selected.map((u) => u.name))].join(', ')} - this model has no upgrade allowance`,
          severity: 'error',
        });
      } else if (selected.length > 0) {
        const covered = new Set<string>();
        for (const allowance of allowances) {
          const famKey = allowanceFamilyKey(allowance);
          const famIds = new Set((famKey ? faction[famKey] ?? [] : []).map((u) => u.id));
          const famSelected = selected.filter((u) => famIds.has(u.id));
          famSelected.forEach((u) => covered.add(u.id));
          if (allowance.max_points !== undefined) {
            const spent = famSelected.reduce((s, u) => s + upgradeCost(u, unit, entry.quantity), 0);
            if (spent > allowance.max_points) {
              issues.push({
                category: 'Upgrades',
                message: `${unit.name}: ${spent} pts of upgrades exceeds the ${allowance.max_points} pt allowance`,
                severity: 'error',
              });
            }
          }
          // Count-capped families (Incantation Scrolls, Chaotic Traits) limit
          // how many, not how much.
          if (allowance.max_count !== undefined && famSelected.length > allowance.max_count) {
            issues.push({
              category: 'Upgrades',
              message: `${unit.name}: ${famSelected.length} taken, but may purchase up to ${allowance.max_count}`,
              severity: 'error',
            });
          }
        }
        // Selections outside every family this model has an allowance for
        const stray = selected.filter((u) => !covered.has(u.id));
        if (stray.length > 0) {
          issues.push({
            category: 'Upgrades',
            message: `${unit.name}: may not purchase ${[...new Set(stray.map((u) => u.name))].join(', ')} - this model has no matching upgrade allowance`,
            severity: 'error',
          });
        }
      }
      // Per-model copies. Most upgrades allow one; a few say "0-2 per model".
      const perModel = new Map<string, number>();
      for (const id of entry.selectedUpgradeIds) {
        perModel.set(id, (perModel.get(id) ?? 0) + 1);
      }
      for (const [id, count] of perModel) {
        const up = pool.find((u) => u.id === id);
        if (!up) continue;
        const cap = maxPerModel(up);
        if (count > cap) {
          issues.push({
            category: 'Upgrades',
            message: cap === 1
              ? `${unit.name}: ${up.name} taken more than once on the same model`
              : `${unit.name}: ${up.name} taken ${count} times, but is limited to ${cap} per model`,
            severity: 'error',
          });
        }
      }

      // Model-level restrictions (troop type, Chaos god, unit type, Wizard level)
      for (const up of new Set(selected)) {
        const { eligible, reason } = upgradeEligibility(up, unit, entry, faction);
        if (!eligible) {
          issues.push({
            category: 'Upgrades',
            message: `${unit.name}: cannot take ${up.name} - ${reason}`,
            severity: 'error',
          });
        }
      }
    }

    // "may only be chosen once per army" - counted across every model in the list
    for (const [id, count] of armyWideCounts) {
      if (count < 2) continue;
      const up = pool.find((u) => u.id === id);
      if (!up || !isOncePerArmy(up)) continue;
      issues.push({
        category: 'Upgrades',
        message: `${up.name} is taken ${count} times, but may only be chosen once per army`,
        severity: 'error',
      });
    }

    // "0-1 unit per 1,000 points" upgrades (Longstriders): counts non-character
    // units carrying the upgrade; characters are unconstrained.
    for (const [id] of armyWideCounts) {
      const up = pool.find((u) => u.id === id);
      const perThousand = up?.restriction?.units_per_1000;
      if (!up || perThousand === undefined) continue;
      const unitCount = army.entries.filter((e) => {
        if (!(e.selectedUpgradeIds ?? []).includes(id)) return false;
        const u = faction.units.find((x) => x.id === e.unitId);
        return u !== undefined && u.category !== 'character';
      }).length;
      const allowed = per1000(perThousand);
      if (unitCount > allowed) {
        issues.push({
          category: 'Upgrades',
          message: `${up.name}: ${unitCount} units - max ${perThousand} unit${perThousand === 1 ? '' : 's'} per 1,000 pts (${allowed} at ${limit} pts)`,
          severity: 'error',
        });
      }
    }
    // "no two characters in your army can have the same combination of Incantation Scrolls"
    if ((faction.incantation_scrolls ?? []).length > 0) {
      const scrollIds = new Set((faction.incantation_scrolls ?? []).map((u) => u.id));
      const seenCombos = new Map<string, string>();
      for (const entry of army.entries) {
        const mine = (entry.selectedUpgradeIds ?? []).filter((id) => scrollIds.has(id));
        if (mine.length === 0) continue;
        const unit = faction.units.find((u) => u.id === entry.unitId);
        const key = [...new Set(mine)].sort().join('|');
        const previous = seenCombos.get(key);
        if (previous) {
          issues.push({
            category: 'Upgrades',
            message: `${unit?.name ?? 'A character'} and ${previous} have the same combination of Incantation Scrolls - no two characters may share one`,
            severity: 'error',
          });
        } else {
          seenCombos.set(key, unit?.name ?? 'another character');
        }
      }
    }

  }

  // --- Honour-family upgrades: once per army where the book says so ---
  // Bretonnian Virtues ("Each Virtue may only be chosen once per army") and
  // any other single-select honour carrying the same restriction text.
  {
    const honourCounts = new Map<string, number>();
    for (const e of army.entries) {
      if (e.selectedVirtueId) {
        honourCounts.set(e.selectedVirtueId, (honourCounts.get(e.selectedVirtueId) ?? 0) + 1);
      }
    }
    for (const [honourId, count] of honourCounts) {
      if (count < 2) continue;
      const honour = honourPool(faction).find((h) => h.id === honourId);
      if (honour && (honour.restrictions ?? '').toLowerCase().includes('once per army')) {
        issues.push({
          category: 'Upgrades',
          message: `${honour.name} is taken ${count} times, but may only be chosen once per army`,
          severity: 'error',
        });
      }
    }
  }

  // --- Magic items: per-model allowance and standard caps ---
  // The picker enforces these at selection time, but imported lists (QR codes,
  // shared links) bypass the picker entirely, so the validator must hold the
  // line too. BSB standards live on selectedBsbStandardId and have no points
  // cap, so they are not checked here.
  for (const entry of army.entries) {
    const unit = faction.units.find((u) => u.id === entry.unitId);
    if (!unit || !(entry.selectedMagicItemIds ?? []).length) continue;
    const items = (entry.selectedMagicItemIds ?? [])
      .map((itemId) => faction.magic_items.find((i) => i.id === itemId))
      .filter((i): i is NonNullable<typeof i> => Boolean(i));

    const standards = items.filter((i) => i.category === 'magic_standard');
    const personal = items.filter((i) => i.category !== 'magic_standard');

    // Format- and origin-gated items. Battle March items are only legal in a
    // Battle March muster list; in a two-parent Army of Infamy an item tagged
    // with an origin needs the model to be of that origin - fixed by the book
    // (Hordes of Chaos) or granted by the selected Infamous Origin (Renegade
    // Crowns). The picker filters these out; imported lists bypass the picker.
    const intrinsicOrigin = (unit as { origin?: string }).origin;
    const unlockedOrigin = entry.selectedVirtueId
      ? honourPool(faction).find((h) => h.id === entry.selectedVirtueId)?.unlocks_origin
      : undefined;
    const prettyFaction = (id: string) => id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const honourFreeEquipNames = entry.selectedVirtueId
      ? (honourPool(faction).find((h) => h.id === entry.selectedVirtueId)?.free_equipment ?? []).map((fe) => fe.name)
      : [];
    for (const item of items) {
      if ((item as { source?: string }).source === 'battle_march' && !isBattleMarchList) {
        issues.push({
          category: 'Magic items',
          message: `${unit.name}: ${item.name} is a Battle March item - only legal in a Battle March list`,
          severity: 'error',
        });
      }
      // Rulebook p.341: only a model equipped with (or with the option to be
      // equipped with) armour, a shield or barding may buy the magical
      // equivalent. The picker hides these; imported lists bypass the picker.
      // A selected honour's free equipment (Blood of Caledor) counts as access.
      if (!magicArmourAllowed(item, unit, honourFreeEquipNames)) {
        issues.push({
          category: 'Magic items',
          message: `${unit.name}: ${item.name} - only a model with armour, a shield or barding (or the option to take them) may buy the magical equivalent`,
          severity: 'error',
        });
      }
      const itemOrigin = (item as { origin?: string }).origin;
      const itemIsCommon = (item as { is_common?: boolean }).is_common === true;
      if (intrinsicOrigin) {
        // A borrowed model buys from its parent book plus the Commons - not
        // even the borrowing faction's own list (owner ruling 2026-08-08).
        if (!itemIsCommon && itemOrigin !== intrinsicOrigin) {
          issues.push({
            category: 'Magic items',
            message: `${unit.name}: ${item.name} - this model may only carry ${prettyFaction(intrinsicOrigin)} or Common magic items`,
            severity: 'error',
          });
        }
      } else if (itemOrigin && itemOrigin !== unlockedOrigin) {
        issues.push({
          category: 'Magic items',
          message: `${unit.name}: ${item.name} is from the ${prettyFaction(itemOrigin)} list - this model does not have that origin`,
          severity: 'error',
        });
      }
    }

    // Personal items need an allowance; where the allowance belongs to a unit
    // champion ("Tomb Captain may purchase magic items…"), the champion must be
    // included - the same gate the picker applies.
    const itemAllowance = unit.options?.find(
      (o) => o.max_points !== undefined && /magic item/i.test(o.description),
    );
    const allowanceActive =
      itemAllowance && (!unit.command?.some((c) => c.role === 'champion') || entry.includeChampion);
    if (personal.length > 0) {
      if (!allowanceActive) {
        issues.push({
          category: 'Magic items',
          message: `${unit.name}: may not carry ${personal.map((i) => i.name).join(', ')} - no active magic items allowance`,
          severity: 'error',
        });
      } else {
        const spent = personal.reduce((s, i) => s + (i.points ?? 0), 0);
        if (spent > itemAllowance.max_points!) {
          issues.push({
            category: 'Magic items',
            message: `${unit.name}: ${spent} pts of magic items exceeds the ${itemAllowance.max_points} pt allowance`,
            severity: 'error',
          });
        }
      }
    }

    if (standards.length > 0) {
      const cap = magicStandardCap(unit);
      if (cap === 0 || !entry.includeStandard) {
        issues.push({
          category: 'Magic items',
          message: `${unit.name}: a magic standard requires a Standard Bearer${cap === 0 ? ', and this unit may not purchase one' : ''}`,
          severity: 'error',
        });
      } else {
        if (standards.length > 1) {
          issues.push({
            category: 'Magic items',
            message: `${unit.name}: ${standards.length} magic standards - a Standard Bearer may carry one`,
            severity: 'error',
          });
        }
        const spent = standards.reduce((s, i) => s + (i.points ?? 0), 0);
        if (spent > cap) {
          issues.push({
            category: 'Magic items',
            message: `${unit.name}: ${spent} pt magic standard exceeds the ${cap} pt limit`,
            severity: 'error',
          });
        }
      }
    }
  }

  // --- Magic items: once-per-army uniqueness (core rulebook rule) ---
  issues.push(...findDuplicateMagicItems(army, faction));

  if (limit === 0) return issues;

  // --- Composition rules ---
  const comp = faction.army_compositions.find((c) => c.id === army.compositionId);
  if (comp) {
    for (const rule of comp.rules) {
      // Per-unit count rules (unit_ids specified)
      if (rule.unit_ids?.length) {
        const unitCount = army.entries.filter((e) => rule.unit_ids!.includes(e.unitId)).length;
        const unitNames = rule.unit_ids
          .map((id) => faction.units.find((u) => u.id === id)?.name ?? id)
          .join(' / ');

        // 'max' and 'max_per_army' are data-entry aliases for max_count (army-wide cap)
        if (rule.limit_type === 'max_count' || rule.limit_type === 'max' || rule.limit_type === 'max_per_army') {
          if (unitCount > rule.limit_value) {
            issues.push({
              category: 'Unit limit',
              message: `${unitNames}: ${unitCount} units - max ${rule.limit_value}`,
              severity: 'error',
            });
          }
        }

        if (rule.limit_type === 'max_per_1000_pts') {
          const max = per1000(rule.limit_value);
          if (unitCount > max) {
            issues.push({
              category: 'Unit limit',
              message: `${unitNames}: ${unitCount} units - max ${rule.limit_value} per 1,000 pts (${max} at ${limit} pts)`,
              severity: 'error',
            });
          }
        }

        if (rule.limit_type === 'min_per_1000_pts') {
          const min = Math.floor(limit / 1000) * rule.limit_value;
          if (pts.total > 0 && unitCount < min) {
            issues.push({
              category: 'Unit requirement',
              message: `${unitNames}: ${unitCount} units - need at least ${rule.limit_value} per 1,000 pts (${min} at ${limit} pts)`,
              severity: 'error',
            });
          }
        }

        if (rule.limit_type === 'min_count') {
          if (pts.total > 0 && unitCount < rule.limit_value) {
            issues.push({
              category: 'Unit requirement',
              message: `${unitNames}: ${unitCount} units - must include at least ${rule.limit_value}`,
              severity: 'error',
            });
          }
        }

        if (rule.limit_type === 'max_per_character') {
          // Count qualifying characters (unlocking characters) in the army
          const charIds = rule.character_unit_ids ?? [];
          const charCount = army.entries.filter((e) => charIds.includes(e.unitId)).length;
          const max = charCount * rule.limit_value;
          if (unitCount > max) {
            const charNames = charIds
              .map((id) => faction.units.find((u) => u.id === id)?.name ?? id)
              .filter((n, i, arr) => arr.indexOf(n) === i)
              .join(' / ');
            issues.push({
              category: 'Unit limit',
              message: `${unitNames}: ${unitCount} unit${unitCount !== 1 ? 's' : ''} - max ${rule.limit_value} per ${charNames} (${charCount} in army → max ${max})`,
              severity: 'error',
            });
          }
        }

        if (rule.limit_type === 'conditional') {
          // Warn if these units are present but the army's General does not qualify.
          if (unitCount > 0 && rule.general_unit_ids?.length) {
            const generalPresent = generalMatches(army, rule.general_unit_ids);
            if (!generalPresent) {
              const generalNames = (rule.general_unit_ids ?? [])
                .map((id) => faction.units.find((u) => u.id === id)?.name ?? id)
                .join(' or ');
              issues.push({
                category: 'General requirement',
                message: `${unitNames}: requires General to be ${generalNames}`,
                severity: 'warning',
              });
            } else if (unitCount > rule.limit_value) {
              issues.push({
                category: 'Unit limit',
                message: `${unitNames}: ${unitCount} unit${unitCount !== 1 ? 's' : ''} - max ${rule.limit_value} under this condition`,
                severity: 'error',
              });
            }
          }
        }

        if (
          (rule.limit_type === 'conditional_honour_count' || rule.limit_type === 'conditional_honour_per_1000_pts') &&
          unitCount > 0 &&
          conditionalHonourApplies(rule, army, faction)
        ) {
          // Advisory: calcCategoryPoints is counting these units in the conditional slot.
          const cap = rule.limit_type === 'conditional_honour_per_1000_pts'
            ? Math.max(1, Math.floor(limit / 1000)) * rule.limit_value
            : rule.limit_value;
          const honourName = rule.required_honour
            ? honourPool(faction)
                .find((v) => v.id === rule.required_honour)?.name ?? rule.required_honour
            : (rule.general_unit_ids ?? [])
                .map((id) => faction.units.find((u) => u.id === id)?.name ?? id)
                .join(' or ');
          issues.push({
            category: 'Conditional slot',
            message: `${unitNames}: up to ${cap} counted as ${capitalise(rule.category)} - requires your General to ${rule.required_honour ? `have the ${honourName}` : `be a ${honourName}`}`,
            severity: 'warning',
          });
        }

        continue; // unit_ids rules don't also do category % checks
      }

      // Category percentage rules
      const catPts = pts[rule.category as keyof CategoryPoints] ?? 0;
      const pct = (catPts / limit) * 100;

      if (rule.limit_type === 'max_percent' && pct > rule.limit_value) {
        issues.push({
          category: rule.category,
          message: `${capitalise(rule.category)} is ${pct.toFixed(1)}% - max ${rule.limit_value}%`,
          severity: 'error',
        });
      }
      if (rule.limit_type === 'min_percent' && pts.total > 0 && pct < rule.limit_value) {
        issues.push({
          category: rule.category,
          message: `${capitalise(rule.category)} is ${pct.toFixed(1)}% - minimum ${rule.limit_value}%`,
          severity: 'error',
        });
      }
    }
  }

  // --- Option-level 0-N per 1,000 pts constraints (parsed from description text) ---
  // The constraint lives in the description string, e.g.:
  //   "Replace Open Order with Skirmishers (0-1 unit per 1,000 pts)"
  //   "0-1 unit per 1,000 points may have the Drilled special rule"
  //   "0-1 unit per 1,000 points may: ..." (choice group header)
  // We count how many army entries have each such option selected and compare
  // against floor(limit / 1000) * N.
  const PER_1000_RE = /\b0-(\d+)\b.*?\bper\s+1[,.]?000\b/i;

  const optionPer1000 = new Map<string, { max: number; count: number }>();
  for (const entry of army.entries) {
    const unit = faction.units.find((u) => u.id === entry.unitId);
    if (!unit) continue;
    for (const opt of unit.options ?? []) {
      const m = PER_1000_RE.exec(opt.description ?? '');
      if (!m) continue;
      const maxPer1000 = parseInt(m[1], 10);

      // Determine whether this entry has the option active. Magic standard
      // allowances ("0-1 unit per 1,000 points may purchase a magic standard…")
      // are exercised by selecting a standard item, not by ticking the option,
      // so they are counted from selectedMagicItemIds instead.
      const isStandardAllowance = opt.max_points !== undefined && /magic standard/i.test(opt.description ?? '');
      const isSelected = isStandardAllowance
        ? entry.includeStandard &&
          (entry.selectedMagicItemIds ?? []).some(
            (itemId) => faction.magic_items.find((i) => i.id === itemId)?.category === 'magic_standard',
          )
        : opt.choices
          ? opt.choices.some((c) => entry.selectedOptions.includes(c.description))
          : entry.selectedOptions.includes(opt.description);
      if (!isSelected) continue;

      const rec = optionPer1000.get(opt.description) ?? { max: maxPer1000, count: 0 };
      rec.count += 1;
      optionPer1000.set(opt.description, rec);
    }
  }
  for (const [desc, { max, count }] of optionPer1000) {
    const allowed = per1000(max);
    if (count > allowed) {
      // Strip the inline constraint annotation for a cleaner display label
      const label = desc
        .replace(/\s*[[(]?0-\d+(?:\s+\w+)?\s+per\s+1[,.]?000\s*(?:pts?|points)[\])]?/gi, '')
        .replace(/\s*:\s*$/, '')
        .trim() || desc;
      issues.push({
        category: 'Unit limit',
        message: `"${label}": ${count} unit${count !== 1 ? 's' : ''} - max ${max} per 1,000 pts (${allowed} at ${limit} pts)`,
        severity: 'error',
      });
    }
  }

  // --- Matched Play: Grand Melee - no single unit/character > 25% of army ---
  if (army.matchedPlayFormats.includes('grand_melee')) {
    for (const entry of army.entries) {
      const unit = faction.units.find((u) => u.id === entry.unitId);
      if (!unit) continue;
      const entryPts = calcEntryPoints(unit, entry, faction);
      const pct = (entryPts / limit) * 100;
      if (pct > 25) {
        issues.push({
          category: 'Grand Melee',
          message: `${unit.name} costs ${entryPts} pts (${pct.toFixed(1)}%) - max 25% per unit`,
          severity: 'error',
        });
      }
    }
  }

  // --- Matched Play: Combined Arms - max identical units (using effective category) ---
  if (army.matchedPlayFormats.includes('combined_arms')) {
    const unitCounts = new Map<string, number>();
    for (const entry of army.entries) {
      unitCounts.set(entry.unitId, (unitCounts.get(entry.unitId) ?? 0) + 1);
    }
    const caComposition = faction.army_compositions.find((c) => c.id === army.compositionId);
    const isAoI = isArmyOfInfamy(caComposition);
    const caSubOrder = army.subOrderId
      ? caComposition?.sub_orders?.find((s) => s.id === army.subOrderId)
      : undefined;
    for (const [unitId, count] of unitCounts) {
      const unit = faction.units.find((u) => u.id === unitId);
      if (!unit) continue;
      const cat = getEffectiveListCategory(unit, army.compositionId, isAoI, caSubOrder);
      const maxAllowed =
        cat === 'characters' ? 3
        : cat === 'core' ? 4
        : cat === 'special' ? 3
        : cat === 'rare' ? 2
        : null;
      if (maxAllowed !== null && count > maxAllowed) {
        issues.push({
          category: 'Combined Arms',
          message: `${unit.name}: ${count} entries - max ${maxAllowed} identical ${cat ?? 'units'}`,
          severity: 'error',
        });
      }
    }
  }

  // --- Matched Play: Battle March (Warhammer: the Old World - Battle March) ---
  if (army.matchedPlayFormats.includes('battle_march')) {
    if (limit < 400 || limit > 750) {
      issues.push({
        category: 'Battle March',
        message: `Army is ${limit} pts - Battle March is played at 400-750 pts`,
        severity: 'error',
      });
    } else if (limit % 50 !== 0) {
      issues.push({
        category: 'Battle March',
        message: `Army is ${limit} pts - Battle March points rise in 50 pt increments`,
        severity: 'warning',
      });
    }

    const bmComposition = faction.army_compositions.find((c) => c.id === army.compositionId);
    const bmIsAoI = isArmyOfInfamy(bmComposition);
    const bmSubOrder = army.subOrderId
      ? bmComposition?.sub_orders?.find((s) => s.id === army.subOrderId)
      : undefined;

    // Two non-character units minimum. War machines count; swarms and war beasts do not.
    let scoringUnits = 0;
    for (const entry of army.entries) {
      const unit = faction.units.find((u) => u.id === entry.unitId);
      if (!unit || unit.category === 'character' || unit.category === 'mount') continue;
      const tt = (unit.troop_type ?? '').toLowerCase();
      if (tt.includes('swarms') || tt.includes('war beasts')) continue;
      scoringUnits += 1;
    }
    if (scoringUnits < 2) {
      issues.push({
        category: 'Battle March',
        message: `${scoringUnits} non-character unit${scoringUnits === 1 ? '' : 's'} - Battle March requires at least 2 (swarms and war beasts do not count)`,
        severity: 'error',
      });
    }

    for (const entry of army.entries) {
      const unit = faction.units.find((u) => u.id === entry.unitId);
      if (!unit) continue;
      const entryPts = calcEntryPoints(unit, entry, faction);
      const pct = (entryPts / limit) * 100;
      const cat = getEffectiveListCategory(unit, army.compositionId, bmIsAoI, bmSubOrder);

      const cap =
        unit.category === 'character' ? 25
        : cat === 'core' ? 35
        : cat === 'special' ? 30
        : cat === 'rare' || cat === 'mercenaries' ? 25
        : null;
      if (cap !== null && pct > cap) {
        const what = unit.category === 'character' ? 'a single character' : `a single ${cat} unit`;
        issues.push({
          category: 'Battle March',
          message: `${unit.name} costs ${entryPts} pts (${pct.toFixed(1)}%) - max ${cap}% on ${what}`,
          severity: 'error',
        });
      }

      const us = calcUnitStrength(unit, entry.quantity);
      if (us > 20) {
        issues.push({
          category: 'Battle March',
          message: `${unit.name} has Unit Strength ${us} - no unit may exceed Unit Strength 20`,
          severity: 'error',
        });
      }
    }

    // "0-X per 1,000 points": a Battle March list may include a single such
    // option in total - a unit, a character, or an upgrade within a unit's options.
    const per1000Picks: string[] = [];
    const per1000UnitIds = new Set<string>();
    for (const rule of bmComposition?.rules ?? []) {
      if (rule.limit_type !== 'max_per_1000_pts') continue;
      for (const id of rule.unit_ids ?? []) per1000UnitIds.add(id);
    }
    for (const entry of army.entries) {
      const unit = faction.units.find((u) => u.id === entry.unitId);
      if (!unit) continue;
      if (per1000UnitIds.has(unit.id)) per1000Picks.push(unit.name);
      for (const opt of unit.options ?? []) {
        const takeIf = (desc: string, per1k: number | undefined) => {
          if (per1k === undefined || !entry.selectedOptions.includes(desc)) return;
          per1000Picks.push(`${unit.name}: ${desc}`);
        };
        takeIf(opt.description, opt.max_per_1000_pts);
        for (const c of opt.choices ?? []) takeIf(c.description, c.max_per_1000_pts);
      }
    }
    if (per1000Picks.length > 1) {
      issues.push({
        category: 'Battle March',
        message: `${per1000Picks.length} "0-X per 1,000 pts" options taken (${per1000Picks.join('; ')}) - a Battle March list may include only one`,
        severity: 'error',
      });
    }
  }

  // --- Option-level army caps ("0-1 unit per 1,000 points may…") ---
  // Counted per pool: by default one pool per unit id + option description;
  // cap_pool shares a pool across units covered by one book entry.
  {
    const capPools = new Map<string, { max: number; count: number; label: string }>();
    const registerCap = (
      unitId: string,
      desc: string,
      selected: boolean,
      max1000: number | undefined,
      maxArmy: number | undefined,
      capPool: string | undefined,
    ) => {
      if (!selected || (max1000 === undefined && maxArmy === undefined)) return;
      const max = max1000 !== undefined ? per1000(max1000) : maxArmy!;
      const key = capPool ?? `${unitId}::${desc}`;
      const pool = capPools.get(key) ?? { max, count: 0, label: desc };
      pool.count += 1;
      pool.max = Math.min(pool.max, max);
      capPools.set(key, pool);
    };
    for (const entry of army.entries) {
      const unit = faction.units.find((u) => u.id === entry.unitId);
      if (!unit) continue;
      for (const opt of unit.options ?? []) {
        if (opt.choices) {
          const groupSelected = opt.choices.some((c) => entry.selectedOptions.includes(c.description));
          registerCap(unit.id, opt.description, groupSelected, opt.max_per_1000_pts, opt.max_per_army, opt.cap_pool);
          for (const c of opt.choices) {
            registerCap(
              unit.id, c.description, entry.selectedOptions.includes(c.description),
              c.max_per_1000_pts, c.max_per_army, c.cap_pool,
            );
          }
        } else {
          registerCap(
            unit.id, opt.description, entry.selectedOptions.includes(opt.description),
            opt.max_per_1000_pts, opt.max_per_army, opt.cap_pool,
          );
        }
      }
    }
    for (const pool of capPools.values()) {
      if (pool.count > pool.max) {
        issues.push({
          category: 'Option limit',
          message: `Only ${pool.max} unit(s) in this army may take '${pool.label}' (${pool.count} selected)`,
          severity: 'error',
        });
      }
    }
  }

  // --- Option prerequisites (mounted-only weapons, standard purchases, General requirements) ---
  for (const entry of army.entries) {
    const unit = faction.units.find((u) => u.id === entry.unitId);
    if (!unit) continue;
    for (const opt of unit.options ?? []) {
      const items = opt.choices ?? [opt];
      for (const it of items) {
        if (!entry.selectedOptions.includes(it.description)) continue;
        if (optionRequiresMount(it.description) && !entryHasMount(entry)) {
          issues.push({
            category: 'Option prerequisite',
            message: `${unit.name}: '${it.description}' requires a mount`,
            severity: 'error',
          });
        }
        if (optionRequiresStandardBearer(it.description) && !entry.includeStandard) {
          issues.push({
            category: 'Option prerequisite',
            message: `${unit.name}: '${it.description}' requires a standard bearer`,
            severity: 'error',
          });
        }
      }
      if (opt.requires_general_unit_ids && items.some((it) => entry.selectedOptions.includes(it.description))) {
        const genEntry = army.generalEntryId ? army.entries.find((e) => e.id === army.generalEntryId) : undefined;
        if (!genEntry || !opt.requires_general_unit_ids.includes(genEntry.unitId)) {
          const names = opt.requires_general_unit_ids
            .map((id) => faction.units.find((u) => u.id === id)?.name ?? id).join(' or ');
          issues.push({
            category: 'Option prerequisite',
            message: `${unit.name}: '${opt.description}' requires ${names} as your General`,
            severity: 'error',
          });
        }
      }
    }
  }

  return issues;
}

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
